// ============================================================
// ProductionTab.jsx  —  Noble Enterprises (tape module)
//
// PHASE 1 OF PRODUCTION: jumbo roll → cores
//

// WHAT COMES OFF THE ROLL
// Only the yards that went into cores. Nothing else.
//
// TRIM COSTS WIDTH, NOT LENGTH
// A jumbo's edges carry no adhesive, so they are cut off before
// coring. A 1280 mm roll gives about 1260 mm of usable core at
// the SAME yardage. The roll's yards drop by yards-into-cores
// only. The trim itself is scrap film, measured in kg, and it
// is calculated from the width lost — the operator never types
// it. Scrap is sold and recorded in Finance → scrap sales.
//
// CORES ARE VARIABLE LENGTH
// One batch can make three 150-yard cores and five 200s. They
// share the core width and spec; only length differs. That is
// why the form takes LINES rather than a single "cores" number.
//
// EVERYTHING HERE IS YARDS. Trim is the one figure in kg,
// because that is how scrap comes off the floor.
//
// HOW THIS FILE IS ORGANISED
//   A. Shared UI bits
//   B. BatchForm
//   C. ProductionTab
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, X, Check, ChevronDown as Chevron } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Trim weight is computed in metres, so yards convert back.
const METERS_TO_YARDS = 1.09361;

// Typical edge trim. Only a starting point — the operator
// overrides it with what the machine actually gave.
const EDGE_TRIM_MM = 20;

const C = {
  brand:      '#f39c12',
  text:       '#1a2332',
  textSoft:   '#64748b',
  textMuted:  '#94a3b8',
  border:     '#e2e8f0',
  borderSoft: '#f1f5f9',
  card:       '#ffffff',
  rowAlt:     '#fafbfc',
  danger:     '#dc2626',
  dangerBg:   '#fee2e2',
  success:    '#059669',
  successBg:  '#d1fae5',
  warning:    '#d97706',
  warningBg:  '#fef3c7',
  info:       '#0284c7',
  infoBg:     '#e0f2fe',
};

const money = (n) => `Rs ${Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 0 })}`;
const yards = (n) => `${Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 0 })} yards`;
const today = () => new Date().toISOString().split('T')[0];
const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB');


// ============================================================
// A. SHARED UI BITS
// ============================================================

const inputStyle = {
  width: '100%', padding: '10px 13px',
  border: `1px solid ${C.border}`, borderRadius: '8px',
  fontSize: '14px', color: C.text, boxSizing: 'border-box',
  fontFamily: 'inherit', background: '#ffffff',
};

const Field = ({ label, children, hint }) => (
  <div>
    <label style={{
      display: 'block', fontSize: '12px', fontWeight: '600',
      color: C.textSoft, marginBottom: '6px',
    }}>
      {label}
    </label>
    {children}
    {hint && <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '5px' }}>{hint}</div>}
  </div>
);

const TextInput = (props) => <input style={inputStyle} {...props} />;

const Btn = ({ variant = 'primary', children, ...props }) => {
  const styles = {
    primary: { background: C.brand,   color: '#ffffff',  border: 'none' },
    ghost:   { background: '#ffffff', color: C.textSoft, border: `1px solid ${C.border}` },
  };
  return (
    <button
      style={{
        ...styles[variant],
        padding: '10px 18px', borderRadius: '8px', fontSize: '14px',
        fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', gap: '7px',
      }}
      {...props}
    >
      {children}
    </button>
  );
};

const IconBtn = ({ danger, title, children, ...props }) => (
  <button
    title={title}
    style={{
      width: '32px', height: '32px', borderRadius: '7px',
      border: `1px solid ${C.border}`, background: '#ffffff',
      color: danger ? C.danger : C.textSoft, cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}
    {...props}
  >
    {children}
  </button>
);

const Banner = ({ type, message, onClose }) => {
  if (!message) return null;
  const isError = type === 'error';
  return (
    <div style={{
      background: isError ? C.dangerBg : C.successBg,
      color:      isError ? C.danger   : C.success,
      padding: '11px 16px', borderRadius: '8px', marginBottom: '16px',
      fontSize: '14px', display: 'flex', alignItems: 'center', gap: '10px',
    }}>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex' }}>
        <X size={16} />
      </button>
    </div>
  );
};

const thStyle = {
  textAlign: 'left', padding: '11px 18px', fontSize: '12px',
  fontWeight: '600', color: C.textSoft, background: C.rowAlt,
  textTransform: 'uppercase', letterSpacing: '0.3px',
};
const tdStyle = {
  padding: '13px 18px', fontSize: '14px', color: C.text,
  borderTop: `1px solid ${C.borderSoft}`,
};

const StatCard = ({ label, value, colour = C.text, sub }) => (
  <div style={{
    background: C.card, border: `1px solid ${C.border}`,
    borderRadius: '10px', padding: '16px 18px',
  }}>
    <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
      {label}
    </div>
    <div style={{ fontSize: '21px', fontWeight: '700', color: colour, marginTop: '4px' }}>
      {value}
    </div>
    {sub && <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px' }}>{sub}</div>}
  </div>
);




// ============================================================
// B. BATCH FORM
//
// The live breakdown is the point: yards into cores come off
// the roll, and the width given up shows as trim in kg.
// ============================================================

const emptyLine = () => ({ length_yards: '', qty: '' });

const BatchForm = ({ form, setForm, rolls, onSave, onCancel, saving }) => {
  const roll = rolls.find(r => String(r.id) === String(form.roll_id));

  const remaining     = roll ? Number(roll.remaining_yards) : 0;
  const gramsPerMeter = roll ? Number(roll.grams_per_meter || 0) : 0;
  const rollWidth     = roll ? Number(roll.width_mm) : 0;

  const coreWidth = Number(form.core_width_mm) || 0;
  const widthOk   = coreWidth > 0 && coreWidth <= rollWidth;

  const lines = form.lines;

  const yardsToCores = lines.reduce((sum, l) => {
    const len = Number(l.length_yards) || 0;
    const qty = Number(l.qty) || 0;
    return sum + (len * qty);
  }, 0);

    const coreCount = lines.reduce((sum, l) => sum + (Number(l.qty) || 0), 0);

  // Most cores of this line's length the roll can still give,
  // after the yards the other lines already take.
  const maxFor = (index) => {
    const len = Number(lines[index].length_yards) || 0;
    if (!roll || !(len > 0)) return null;
    const own   = len * (Number(lines[index].qty) || 0);
    const other = yardsToCores - own;
    return Math.max(0, Math.floor((remaining - other) / len));
  };

    const yardsAfter = remaining - yardsToCores;
  const tooMuch    = yardsToCores > remaining;

  // Trim: width given up, priced as film weight.
  const trimMm = widthOk ? rollWidth - coreWidth : 0;
  const trimKg = (widthOk && gramsPerMeter > 0 && rollWidth > 0)
    ? (yardsToCores / METERS_TO_YARDS) * (trimMm / rollWidth) * gramsPerMeter / 1000
    : 0;

  // Picking a roll prefills the usual trim. The operator changes
  // it when the machine gave something different.
  const pickRoll = (id) => {
    const picked = rolls.find(r => String(r.id) === String(id));
    setForm({
      ...form,
      roll_id: id,
      core_width_mm: picked
        ? String(Math.max(0, Number(picked.width_mm) - EDGE_TRIM_MM))
        : '',
    });
  };

  const setLine = (index, patch) => {
    const next = lines.map((l, i) => (i === index ? { ...l, ...patch } : l));
    setForm({ ...form, lines: next });
  };

  const addLine = () => setForm({ ...form, lines: [...lines, emptyLine()] });

  const removeLine = (index) => {
    const next = lines.filter((_, i) => i !== index);
    setForm({ ...form, lines: next.length ? next : [emptyLine()] });
  };

  return (
    <div style={{ padding: '20px 22px', background: C.rowAlt, borderBottom: `1px solid ${C.borderSoft}` }}>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
        gap: '16px', marginBottom: '18px',
      }}>
        <Field label="Jumbo roll">
          <select
            style={inputStyle}
            value={form.roll_id}
            onChange={e => pickRoll(e.target.value)}
          >
            <option value="">Select roll…</option>
            {rolls.map(r => (
              <option key={r.id} value={r.id}>
                {r.vendor_roll_number} — {yards(r.remaining_yards)} left
              </option>
            ))}
          </select>
        </Field>

        <Field
          label="Core width (mm)"
          hint={roll ? `Roll is ${rollWidth} mm — the edges carry no gum and come off` : undefined}
        >
          <TextInput
            type="number" step="0.01" min="0"
            value={form.core_width_mm}
            onChange={e => setForm({ ...form, core_width_mm: e.target.value })}
            placeholder="e.g. 1260"
          />
        </Field>

        

        <Field label="Production date">
          <TextInput
            type="date"
            max={today()}
            value={form.production_date}
            onChange={e => setForm({ ...form, production_date: e.target.value })}
          />
        </Field>

        <Field label="Notes">
          <TextInput
            value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
            placeholder="Optional"
          />
        </Field>
      </div>

      {/* Core width outside what the roll can give */}
      {roll && coreWidth > rollWidth && (
        <div style={{
          background: C.dangerBg, color: C.danger, borderRadius: '8px',
          padding: '13px 16px', marginBottom: '18px', fontSize: '13px',
        }}>
          Core width cannot exceed the roll's {rollWidth} mm.
        </div>
      )}

      {/* --- core lines --- */}
      <div style={{
        background: '#ffffff', border: `1px solid ${C.border}`,
        borderRadius: '8px', padding: '16px 18px', marginBottom: '18px',
      }}>
        <div style={{
          fontSize: '12px', fontWeight: '600', color: C.textSoft,
          textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '12px',
        }}>
          Cores cut {coreWidth > 0 && (
            <span style={{ color: C.textMuted, fontWeight: '500', textTransform: 'none' }}>
              — all at {coreWidth} mm wide
            </span>
          )}
        </div>

        {lines.map((line, index) => (
          <div key={index} style={{
            display: 'flex', gap: '12px', alignItems: 'flex-end', marginBottom: '10px',
          }}>
            <div style={{ flex: '1 1 160px' }}>
              <label style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600' }}>
                Length (yards)
              </label>
              <TextInput
                type="number" step="0.01" min="0"
                value={line.length_yards}
                onChange={e => setLine(index, { length_yards: e.target.value })}
                placeholder="e.g. 150"
              />
            </div>
            <div style={{ flex: '0 0 120px' }}>
              <label style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600' }}>
                How many
              </label>
              <TextInput
                type="number" min="1" step="1"
                value={line.qty}
                onChange={e => setLine(index, { qty: e.target.value })}
                placeholder="e.g. 5"
              />
            </div>
                        <div style={{ flex: '0 0 auto', paddingBottom: '2px', minWidth: '90px' }}>
              {maxFor(index) !== null && (
                <div style={{ fontSize: '11px', color: C.info, fontWeight: '600', marginBottom: '9px' }}>
                  max {maxFor(index)}
                </div>
              )}
              <div style={{ fontSize: '13px', color: C.textSoft }}>
                {Number(line.length_yards) > 0 && Number(line.qty) > 0
                  ? yards(Number(line.length_yards) * Number(line.qty))
                  : '—'}
              </div>
            </div>
            <div style={{ flex: '0 0 auto', paddingBottom: '2px' }}>
              <IconBtn danger title="Remove line" onClick={() => removeLine(index)}>
                <Trash2 size={15} />
              </IconBtn>
            </div>
          </div>
        ))}

        <Btn variant="ghost" onClick={addLine}>
          <Plus size={15} /> Add another length
        </Btn>
      </div>

      

      {/* Live breakdown */}
            {roll && coreCount > 0 && (
        <div style={{
          background: '#ffffff',
          border: `1px solid ${tooMuch ? C.danger : C.border}`,
          borderRadius: '8px', padding: '16px 18px', marginBottom: '18px',
        }}>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
            gap: '16px',
          }}>
                        {[
              ['Roll has',   yards(remaining),                          C.textSoft],
              ['Into cores', yards(yardsToCores),                       C.success],
              ['Leaves',     yards(Math.max(0, yardsAfter)),            tooMuch ? C.danger : C.text],
              ['Trim width', widthOk ? `${trimMm.toFixed(1)} mm` : '—', C.warning],
              ['Trim scrap', `${trimKg.toFixed(2)} kg`,                 C.warning],
            ].map(([label, value, colour]) => (
              <div key={label}>
                <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
                  {label}
                </div>
                <div style={{ fontSize: '17px', fontWeight: '700', color: colour, marginTop: '3px' }}>
                  {value}
                </div>
              </div>
            ))}
          </div>

          <div style={{
            marginTop: '14px', paddingTop: '12px',
            borderTop: `1px solid ${C.borderSoft}`,
            display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap',
            fontSize: '12px', color: C.textMuted,
          }}>
                       <span>
              {coreCount} core{coreCount === 1 ? '' : 's'}
              {coreWidth > 0 && ` at ${coreWidth} mm`}
              {widthOk && gramsPerMeter > 0 &&
                ` · ${rollWidth} → ${coreWidth} mm at ${gramsPerMeter.toFixed(1)} g/m`}
            </span>
          </div>

          {tooMuch && (
            <div style={{
              marginTop: '12px', padding: '10px 13px', borderRadius: '7px',
              background: C.dangerBg, color: C.danger, fontSize: '13px',
            }}>
                            That is {yards(yardsToCores - remaining)} more than this roll has left.
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px' }}>
        <Btn
          onClick={onSave}
                    disabled={saving || tooMuch || coreCount === 0 || !widthOk}
        >
          <Check size={16} /> {saving ? 'Saving…' : 'Record batch'}
        </Btn>
        <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
      </div>
    </div>
  );
};





const emptyForm = () => ({
  roll_id: '',
  core_width_mm: '',
    lines: [emptyLine()],
  production_date: today(),
  notes: '',
});

const ProductionTab = () => {
  const [batches, setBatches] = useState([]);
  const [rolls, setRolls]     = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm]       = useState(null);
  const [saving, setSaving]   = useState(false);
  const [expandedId, setExpandedId] = useState(null);
    const [detail, setDetail]   = useState({});
  const [banner, setBanner]   = useState({ type: '', message: '' });

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const notify = (type, message) => {
    setBanner({ type, message });
    if (type === 'success') setTimeout(() => setBanner({ type: '', message: '' }), 3000);
  };

  const load = useCallback(async () => {
    try {
      const [b, r, s] = await Promise.all([
        axios.get(`${API}/tape/core-batches`, { headers }),
        axios.get(`${API}/tape/core-batches/rolls/available`, { headers }),
        axios.get(`${API}/tape/core-batches/summary`, { headers }),
      ]);
      setBatches(b.data.data || []);
      setRolls(r.data.data || []);
      setSummary(s.data.data || null);
    } catch {
      notify('error', 'Could not load production. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  // The length breakdown is only fetched when a row is opened —
  // most rows are never expanded.
  const toggleRow = async (row) => {
    if (expandedId === row.id) { setExpandedId(null); return; }
    setExpandedId(row.id);

    if (!detail[row.id]) {
      try {
        const res = await axios.get(`${API}/tape/core-batches/${row.id}`, { headers });
        setDetail(d => ({ ...d, [row.id]: res.data.data }));
      } catch {
        notify('error', 'Could not load that batch');
      }
    }
  };

  const save = async () => {
    const f = form;
    if (!f.roll_id)         return notify('error', 'Pick a jumbo roll');
    if (!f.production_date) return notify('error', 'Production date is required');

    if (!(Number(f.core_width_mm) > 0)) {
      return notify('error', 'Enter the core width in mm');
    }

    const lines = f.lines
      .map(l => ({ length_yards: Number(l.length_yards), qty: Number(l.qty) }))
      .filter(l => l.length_yards > 0 && l.qty > 0);

    if (lines.length === 0) {
      return notify('error', 'Add at least one core length and quantity');
    }
    if (lines.some(l => !Number.isInteger(l.qty))) {
      return notify('error', 'Core quantity must be a whole number');
    }

    setSaving(true);
    try {
      await axios.post(`${API}/tape/core-batches`, {
        roll_id:         f.roll_id,
        core_width_mm:   f.core_width_mm,
               lines,
        production_date: f.production_date,
        notes:           f.notes,
      }, { headers });

      notify('success', 'Core batch recorded');
      setForm(null);
      setDetail({});
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(
      `Delete batch ${row.batch_number}? The yards go back on the roll and its cores leave WIP.`
    )) return;

    try {
      await axios.delete(`${API}/tape/core-batches/${row.id}`, { headers });
      notify('success', 'Batch deleted');
      if (expandedId === row.id) setExpandedId(null);
      setDetail({});
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not delete');
    }
  };

  return (
    <div>
      <Banner
        type={banner.type}
        message={banner.message}
        onClose={() => setBanner({ type: '', message: '' })}
      />

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '14px', marginBottom: '20px',
      }}>
        <StatCard label="Cores in WIP"    value={summary?.cores_in_stock ?? '—'} colour={C.brand} />
        <StatCard label="Made this month" value={summary?.cores_this_month ?? '—'} />
                <StatCard label="Total batches"   value={summary?.batch_count ?? '—'} />
        <StatCard
          label="Trim scrap"
                    value={summary ? `${Number(summary.trim_kg_total || 0).toFixed(1)} kg` : '—'}
                    
          colour={C.warning}
          sub="Sold via Finance → scrap"
        />
      </div>

      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: '12px', overflow: 'hidden',
      }}>
        <div style={{
          padding: '18px 22px', borderBottom: `1px solid ${C.borderSoft}`,
          display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: C.text }}>
              Core production
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: '13px', color: C.textSoft }}>
                           Jumbo rolls trimmed and cut into cores. Trim costs width, not yards.
            </p>
          </div>

          {!form && (
            <Btn onClick={() => setForm(emptyForm())} disabled={rolls.length === 0}>
              <Plus size={16} /> Record batch
            </Btn>
          )}
        </div>

        {!form && rolls.length === 0 && !loading && (
          <div style={{ padding: '14px 22px', background: C.infoBg, fontSize: '13px', color: C.info }}>
            No jumbo rolls with yards left. Receive stock first.
          </div>
        )}

        {form && (
          <BatchForm
            form={form}
            setForm={setForm}
            rolls={rolls}
            onSave={save}
            onCancel={() => setForm(null)}
            saving={saving}
          />
        )}

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Batch</th>
                <th style={thStyle}>Roll</th>
                <th style={thStyle}>Date</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Cores</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>In WIP</th>
                                <th style={{ ...thStyle, textAlign: 'right' }}>Into cores</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Trim</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="8" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '36px' }}>
                    Loading…
                  </td>
                </tr>
              )}

              {!loading && batches.map(row => {
                const isOpen  = expandedId === row.id;
                const inStock = Number(row.cores_in_stock);
                const made    = Number(row.cores_produced);
                                const used    = Number(row.cores_used);
                const d       = detail[row.id];

                return (
                  <React.Fragment key={row.id}>
                    <tr
                      onClick={() => toggleRow(row)}
                      style={{ cursor: 'pointer', background: isOpen ? C.rowAlt : 'transparent' }}
                    >
                      <td style={{ ...tdStyle, fontWeight: '700' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
                          <Chevron
                            size={15}
                            style={{
                              color: C.textMuted,
                              transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                              transition: 'transform 0.15s',
                            }}
                          />
                          {row.batch_number}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        {row.vendor_roll_number}
                        <div style={{ fontSize: '11px', color: C.textMuted }}>{row.spec_label}</div>
                      </td>
                      <td style={{ ...tdStyle, color: C.textSoft }}>{fmtDate(row.production_date)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '600' }}>{made}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <span style={{ fontWeight: '600', color: inStock === 0 ? C.textMuted : C.brand }}>
                          {inStock}
                        </span>
                        {used > 0 && (
                          <div style={{ fontSize: '11px', color: C.textMuted }}>{used} cut</div>
                        )}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: C.textSoft }}>
                        {yards(row.yards_to_cores)}
                      </td>
                                            <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <span style={{ fontWeight: '600', color: C.warning }}>
                          {Number(row.waste_kg || 0).toFixed(2)} kg
                        </span>
                        <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px' }}>
                          {Number(row.width_mm) - Number(row.core_width_mm)} mm off
                        </div>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        {used === 0 && (
                          <IconBtn
                            danger
                            title="Delete batch"
                            onClick={(e) => { e.stopPropagation(); remove(row); }}
                          >
                            <Trash2 size={15} />
                          </IconBtn>
                        )}
                      </td>
                    </tr>

                    {isOpen && (
                      <tr>
                        <td colSpan="8" style={{
                          padding: '18px 22px', background: C.rowAlt,
                          borderTop: `1px solid ${C.borderSoft}`,
                        }}>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                            gap: '12px', marginBottom: '14px',
                          }}>
                            {[
                              ['Spec',           row.spec_label],
                              ['Roll width',     `${Number(row.width_mm)} mm`],
                              ['Core width',     `${Number(row.core_width_mm)} mm`],
                              ['Vendor',         row.vendor_name],
                                                          ['Into cores',     yards(row.yards_to_cores)],
                              ['Trim off',      `${Number(row.width_mm) - Number(row.core_width_mm)} mm`],
                              ['Trim scrap',    `${Number(row.waste_kg || 0).toFixed(2)} kg`],
                              ['Roll now has',   yards(row.roll_remaining_yards)],
                            ].map(([label, value]) => (
                              <div key={label} style={{
                                background: '#ffffff', border: `1px solid ${C.border}`,
                                borderRadius: '8px', padding: '12px 14px',
                              }}>
                                <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
                                  {label}
                                </div>
                                <div style={{ fontSize: '14px', fontWeight: '700', color: C.text, marginTop: '3px' }}>
                                  {value}
                                </div>
                              </div>
                            ))}
                          </div>

                          <div style={{
                            fontSize: '11px', color: C.textMuted, fontWeight: '600',
                            textTransform: 'uppercase', marginBottom: '8px',
                          }}>
                            Cores made
                          </div>

                          {d?.lines?.length ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                              {d.lines.map((l, i) => (
                                <span key={i} style={{
                                  fontSize: '13px', fontWeight: '600', color: C.text,
                                  background: '#ffffff', border: `1px solid ${C.border}`,
                                  borderRadius: '8px', padding: '5px 11px',
                                }}>
                                  {Number(l.length_yards).toLocaleString('en-PK')} yards
                                  <span style={{ color: C.textMuted, fontWeight: '500' }}> × {l.qty}</span>
                                  {Number(l.used) > 0 && (
                                    <span style={{ color: C.textMuted, fontWeight: '500' }}>
                                      {' '}({l.used} cut)
                                    </span>
                                  )}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <div style={{ fontSize: '13px', color: C.textMuted }}>Loading…</div>
                          )}

                          {row.notes && (
                            <div style={{ marginTop: '12px', fontSize: '13px', color: C.textSoft }}>
                              <strong style={{ color: C.text }}>Notes:</strong> {row.notes}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}

              {!loading && batches.length === 0 && (
                <tr>
                  <td colSpan="8" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '40px' }}>
                    No core batches yet. Click "Record batch" to start production.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default ProductionTab;